import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Check, X, RefreshCcw } from 'lucide-react';
import { motion } from 'framer-motion';
import { negotiateAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function NegotiationsPage() {
  const { user } = useAuth();
  const [role, setRole] = useState('seller');
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [counterPrice, setCounterPrice] = useState('');
  const [counterMsg, setCounterMsg] = useState('');
  const [responding, setResponding] = useState(false);

  const fetchNegs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await negotiateAPI.myNegotiations(role);
      setNegotiations(res.data);
    } catch {}
    finally { setLoading(false); }
  }, [role]);

  useEffect(() => { fetchNegs(); }, [fetchNegs]);

  const handleRespond = async (negId, action) => {
    setResponding(true);
    try {
      const res = await negotiateAPI.respond(negId, {
        action,
        counter_price: action === 'counter' ? parseFloat(counterPrice) : undefined,
        message: counterMsg || undefined,
      });
      toast.success(action === 'accept' ? 'Deal accepted!' : action === 'reject' ? 'Offer rejected' : 'Counter sent!');
      setSelected(res.data);
      fetchNegs();
      setCounterPrice('');
      setCounterMsg('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setResponding(false); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-50 pb-20" data-testid="negotiations-page">
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 pt-8 pb-6">
          <Link to="/" className="text-zinc-400 hover:text-zinc-700 mb-4 inline-flex items-center gap-1 text-sm" data-testid="negotiations-back-home-link"><ArrowLeft size={16} /> Home</Link>
          <h1 className="font-display font-bold text-3xl text-zinc-900 mb-2">AI Negotiations</h1>
          <p className="text-zinc-500 text-sm">AI handles the back-and-forth. You just set your terms.</p>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setRole('seller')} className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${role === 'seller' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-500'}`} data-testid="role-seller">As Seller</button>
            <button onClick={() => setRole('buyer')} className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${role === 'buyer' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-500'}`} data-testid="role-buyer">As Buyer</button>
            <button onClick={fetchNegs} className="ml-auto px-3 py-2 text-zinc-400 hover:text-zinc-700" data-testid="negotiations-refresh-button"><RefreshCcw size={16} /></button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="space-y-3">
          {loading ? <div className="text-center py-8 text-zinc-400">Loading...</div> : negotiations.length > 0 ? negotiations.map(neg => (
            <button key={neg.id} onClick={() => setSelected(neg)} className={`w-full text-left p-4 rounded-2xl border transition-all ${selected?.id === neg.id ? 'border-[#00aa55] bg-[#00aa55]/5' : 'border-zinc-200 bg-white hover:border-zinc-300'}`} data-testid={`neg-${neg.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-zinc-900 text-sm truncate">{neg.product_name}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${neg.status === 'active' ? 'bg-blue-100 text-blue-600' : neg.status === 'accepted' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>{neg.status}</span>
              </div>
              <div className="flex justify-between text-xs text-zinc-500">
                <span>Listed: {formatCurrency(neg.listing_price)}</span>
                <span>Offer: {formatCurrency(neg.current_offer)}</span>
              </div>
              <p className="text-xs text-zinc-400 mt-1">Round {neg.rounds}/{neg.max_rounds}</p>
            </button>
          )) : <p className="text-center py-8 text-zinc-400 text-sm">No negotiations yet</p>}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {selected ? (
            <div className="bg-white rounded-2xl border border-zinc-200 p-6" data-testid="neg-detail">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-bold text-zinc-900 text-lg">{selected.product_name}</h2>
                  <p className="text-xs text-zinc-500">Listed: {formatCurrency(selected.listing_price)} | Current offer: {formatCurrency(selected.current_offer)}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${selected.status === 'active' ? 'bg-blue-100 text-blue-600' : selected.status === 'accepted' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>{selected.status}</span>
              </div>

              {/* Messages */}
              <div className="space-y-3 mb-6 max-h-80 overflow-y-auto">
                {selected.messages?.map(msg => (
                  <motion.div key={msg.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className={`p-3 rounded-xl max-w-[85%] ${msg.from === 'ai_buyer' ? 'bg-blue-50 border border-blue-100 mr-auto' : msg.from === 'seller' ? 'bg-zinc-100 border border-zinc-200 ml-auto' : 'bg-zinc-50 mx-auto text-center'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold text-zinc-400">{msg.from === 'ai_buyer' ? 'AI Buyer Agent' : msg.from === 'seller' ? 'You (Seller)' : 'System'}</span>
                      {msg.offer_price && <span className="text-[10px] font-bold text-[#00aa55]">{formatCurrency(msg.offer_price)}</span>}
                    </div>
                    <p className="text-sm text-zinc-700">{msg.text}</p>
                  </motion.div>
                ))}
              </div>

              {/* Actions (seller only) */}
              {role === 'seller' && selected.status === 'active' && (
                <div className="border-t border-zinc-200 pt-4 space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <Button onClick={() => handleRespond(selected.id, 'accept')} disabled={responding} className="bg-green-500 text-white hover:bg-green-600 font-bold" data-testid="accept-btn">
                      <Check size={14} className="mr-1" /> Accept {formatCurrency(selected.current_offer)}
                    </Button>
                    <div className="flex gap-1">
                      <Input type="number" placeholder="Counter $" value={counterPrice} onChange={e => setCounterPrice(e.target.value)} className="text-sm border-zinc-200" data-testid="counter-price-input" />
                      <Button onClick={() => handleRespond(selected.id, 'counter')} disabled={responding || !counterPrice} className="bg-amber-500 text-white hover:bg-amber-600 font-bold px-3" data-testid="counter-btn">
                        Counter
                      </Button>
                    </div>
                    <Button onClick={() => handleRespond(selected.id, 'reject')} disabled={responding} variant="outline" className="border-red-200 text-red-500 hover:bg-red-50 font-bold" data-testid="reject-btn">
                      <X size={14} className="mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              )}

              {selected.status === 'accepted' && selected.final_price && (
                <div className="border-t border-zinc-200 pt-4 text-center">
                  <p className="text-green-600 font-bold text-lg">Deal closed at {formatCurrency(selected.final_price)}</p>
                  <p className="text-xs text-zinc-400 mt-1">Savings: {formatCurrency(selected.listing_price - selected.final_price)} ({((1 - selected.final_price / selected.listing_price) * 100).toFixed(0)}% off)</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center">
              <MessageSquare size={48} className="mx-auto text-zinc-200 mb-4" />
              <p className="text-zinc-400">Select a negotiation to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
